// src/components/ui/GlowLogo.tsx
// Reusable GLOW wordmark, recreated in code from the reference screenshot —
// the live glowhire.biz mark ships as an opaque raster asset with no
// accessible source, so this is a best-effort reconstruction (SVG + CSS),
// not an extraction. Exact letterform kerning will not be pixel identical
// to the source without the original vector file.
import type { CSSProperties } from 'react';
import { brand } from '@/lib/tokens';

type Size = 'sm' | 'md' | 'lg';

const SIZE_MAP: Record<Size, { letter: number; gap: number; label: number; divider: number }> = {
  sm: { letter: 31, gap: 1, label: 14.5, divider: 36 },
  md: { letter: 45, gap: 1.5, label: 17, divider: 50 },
  lg: { letter: 140, gap: 5, label: 40, divider: 150 },
};

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

/** Point on a circle of radius r centred at (c,c); 0deg = east, clockwise. */
function pointOnCircle(c: number, r: number, deg: number) {
  return { x: c + r * Math.cos(toRad(deg)), y: c + r * Math.sin(toRad(deg)) };
}

function arcPath(c: number, r: number, startDeg: number, endDeg: number) {
  const start = pointOnCircle(c, r, startDeg);
  const end = pointOnCircle(c, r, endDeg);
  return `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;
}

/**
 * The GLOW "O" mark — a thick ring cut into four equal arc segments, with
 * openings centred on the cardinal points (top, right, bottom, left). Built
 * from four explicit SVG arcs rather than a dashed circle so the gaps land
 * exactly on the cardinal axes, not an approximation.
 */
function SegmentedO({ size }: { size: number }) {
  const stroke = Math.max(2, Math.round(size * 0.22));
  const r = size / 2 - stroke / 2;
  const c = size / 2;
  const gapDeg = 28; // angular width of each cut-out, centred on 0/90/180/270
  const half = gapDeg / 2;
  // Cardinal centres in this coordinate system: 0=east(right), 90=south(bottom),
  // 180=west(left), 270=north(top) — i.e. exactly right/bottom/left/top.
  const segments = [0, 90, 180, 270].map((cardinal) =>
    arcPath(c, r, cardinal + half, cardinal + 90 - half),
  );
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      {segments.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="butt"
        />
      ))}
    </svg>
  );
}

export function GlowLogo({
  label,
  size = 'md',
  className,
  style,
}: {
  /** Secondary label to the right of the wordmark. Omit to render the mark alone. */
  label?: string;
  size?: Size;
  className?: string;
  style?: CSSProperties;
}) {
  const s = SIZE_MAP[size];
  const wordmarkStyle: CSSProperties = {
    fontFamily: 'var(--font-manrope), system-ui, sans-serif',
    fontWeight: 700,
    fontSize: s.letter,
    lineHeight: 1,
    letterSpacing: '-0.02em',
    color: brand.accent,
  };

  return (
    <div
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: s.divider * 0.36, ...style }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: s.gap, ...wordmarkStyle }}>
        <span>G</span>
        <span>L</span>
        <SegmentedO size={s.letter * 0.82} />
        <span>W</span>
      </div>
      {label && (
        <>
          <span
            aria-hidden
            style={{ width: 1, height: s.divider, background: brand.hairlineFull, flexShrink: 0 }}
          />
          <span
            style={{
              fontFamily: 'var(--font-manrope)',
              fontWeight: 600,
              fontSize: s.label,
              lineHeight: 1.2,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: brand.onSurface,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
        </>
      )}
    </div>
  );
}
